import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import { IReactChildrenOnly } from "@custom-types/types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchCurrentUser,
  getLoggedInUser,
} from "@store/user/user-slice-thunk";
import { useEffect, useRef, useMemo, useCallback } from "react";
import { fetchUserSettings } from "@store/settings/settings-thunks";
import { loggedInUserSelector } from "@store/user/user-selector";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { isSupportedLanguageObject } from "@flat-ui";
import { useLocalize } from "@faro-lotv/foreign-observers";
import { SettingsApiUtils } from "@stellar/settings-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";

/** User settings that are fetched on initial load */
export const USER_SETTINGS_TO_FETCH = ["unit_measurement", "language"] as const;

export type DefaultUserSetting = (typeof USER_SETTINGS_TO_FETCH)[number];

/**
 * Centralized loader that fetches the logged-in user data.
 * This prevents duplicate API calls throughout the application.
 *
 * The loader shows a loading spinner while fetching user data and handles errors appropriately.
 * For the account settings page, it allows guest access and doesn't require authentication.
 */
export function UserLoader({ children }: IReactChildrenOnly): JSX.Element {
  const dispatch = useAppDispatch();
  const { setLoadingSpinner } = useLoadingSpinner();
  /** Ref to track if user has already been fetched */
  const isUserFetchedRef = useRef(false);
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const { handleErrorWithToast } = useErrorContext();
  const { availableLanguages, setLanguage } = useLocalize();

  const supportedLanguages = useMemo(() => {
    return availableLanguages.filter(isSupportedLanguageObject);
  }, [availableLanguages]);

  /**
   * Initialize the application language based on user settings.
   * If the user language is supported and the user has set the language already,
   * then update the LocalizeJS language to the user language.
   */
  const initializeLanguage = useCallback(
    (languageSetting: ReturnType<typeof userSettingsSelector>["language"]) => {
      if (!languageSetting) {
        return;
      }

      const languageObject = supportedLanguages.find(
        ({ code }) => code === languageSetting.value
      );

      if (
        languageObject &&
        languageSetting.modifiedBy &&
        !SettingsApiUtils.isDefaultSettings(languageSetting.modifiedBy)
      ) {
        setLanguage(languageObject.code);
      }
    },

    // "setLanguage" dependency is unexpectedly triggering undesired re-renders. As workaround this dependency
    // is removed from the dependencies array.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportedLanguages]
  );

  /** Fetch the logged-in user data once */
  /** Ref to track if user in selected company context has already been fetched */
  const isUserInCompanyFetchedRef = useRef(false);

  const selectedSdbCompanyId = useAppSelector(selectedSdbCompanyIdSelector);

  /**
   * Fetch the logged-in user details, independent of the selected company.
   * In comparison to endpoint for the current user, the following attributes are included
   * - `isMailVerified`
   * - `authenticationInfo`
   * - `fueFlags`
   */
  useEffect(() => {
    async function fetchLoggedInUserViaStore(): Promise<void> {
      // Skip if user is already fetched once
      if (isUserFetchedRef.current) {
        return;
      }
      try {
        setLoadingSpinner(true);
        await dispatch(getLoggedInUser());
      } catch {
        // Note: Authentication errors (e.g., 401/403) are globally handled by AuthProvider,
        // so explicit error handling here is unnecessary.
        // For account-setting pages, errors are not shown because these routes support guest access (no login required).
      } finally {
        isUserFetchedRef.current = true;
        setLoadingSpinner(false);
      }
    }

    void fetchLoggedInUserViaStore();
  }, [dispatch, setLoadingSpinner]);

  /**
   * When loggedInUser is available, fetch and store all user settings in redux,
   * then initialize the application language based on the fetched settings.
   * If the user language is supported and the user has set the language already,
   * then update the LocalizeJS language to the user language.
   */
  useEffect(() => {
    const userId = loggedInUser?.id;
    if (!userId) {
      return;
    }

    async function fetchBatchUserSettings(
      userIdForFetch: string
    ): Promise<void> {
      try {
        const userSettingsResult = await dispatch(
          fetchUserSettings({
            identifiers: [...USER_SETTINGS_TO_FETCH],
            userId: userIdForFetch,
          })
        ).unwrap();

        initializeLanguage(userSettingsResult.language);
      } catch (error) {
        handleErrorWithToast({
          id: `fetchBatchUserSettings-${Date.now().toString()}`,
          title: "Failed to fetch user settings.",
          error,
        });
      }
    }
    void fetchBatchUserSettings(userId);
  }, [loggedInUser, dispatch, handleErrorWithToast, initializeLanguage]);

  /**
   * Fetch the current logged-in user within the context of the selected company.
   * It has some duplicated attributes with the above effect, but is necessary to ensure
   * that the user data is accurate for the selected company context.
   * In comparison to endpoint for the logged in user, the following attributes are included
   * - `role`
   * - `permissions`
   */
  useEffect(() => {
    async function fetchCurrentUserViaStore(): Promise<void> {
      if (!selectedSdbCompanyId || isUserInCompanyFetchedRef.current) {
        return;
      }
      try {
        setLoadingSpinner(true);
        await dispatch(fetchCurrentUser({ companyId: selectedSdbCompanyId }));
      } catch {
        // Note: Error is already handled, if this fails, the user will be redirected to a critical error page
      } finally {
        isUserInCompanyFetchedRef.current = true;
        setLoadingSpinner(false);
      }
    }

    void fetchCurrentUserViaStore();
  }, [dispatch, selectedSdbCompanyId, setLoadingSpinner]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
